import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `checkout:` scope (F45/F46,
 * `/checkout/success` + `/checkout/error`) — see `apps/web/src/routes/
 * settings/drive/drive.i18n.ts`'s doc comment for the pattern this follows.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/checkout.json`.
 */
const en = {
  'success.confirming.title': 'Thanks! Confirming your upgrade…',
  'success.confirming.creditPack.title': 'Thanks! Confirming your purchase…',
  'success.confirming.description': 'This usually takes a few seconds.',
  'success.confirming.action': 'Check billing status',
  'success.confirmed.subscription.title': 'PRO unlocked',
  'success.confirmed.subscription.description': 'You now have unlimited expand, cut-and-continue, difficulty elevation, translation, and illustrations.',
  'success.confirmed.subscription.renews': 'Renews {date}',
  'success.confirmed.creditPack.title': 'Bought “{packName}” — +{count, plural, one {# credit} other {# credits}}',
  'success.action.start': 'Start using PRO',
  'success.action.continue': 'Continue',
  'success.stillConfirming.title': 'Still confirming…',
  'success.stillConfirming.description': 'This is taking longer than usual. Your purchase is safe — check back here in a minute, or refresh.',
  'success.stillConfirming.action': 'Go to Billing',

  'error.cancelled.title': 'No changes made — you’re still on Free',
  'error.cancelled.description': 'Checkout was cancelled. You can try again any time.',
  'error.cancelled.action': 'Try again',
  'error.declined.title': 'Payment declined',
  'error.declined.description': 'Your payment method was declined. Update it and try again.',
  'error.declined.action': 'Update payment method',
  'error.pastDue.title': 'Your PRO payment didn’t go through',
  'error.pastDue.description': 'Update your payment method by {date} to keep PRO.',
  'error.pastDue.descriptionNoDate': 'Update your payment method to keep PRO.',
  'error.pastDue.action': 'Update payment method',
  'error.backToBilling': 'Back to Billing',
} as const;

const ro: Record<keyof typeof en, string> = {
  'success.confirming.title': 'Mulțumim! Confirmăm upgrade-ul tău…',
  'success.confirming.creditPack.title': 'Mulțumim! Confirmăm achiziția ta…',
  'success.confirming.description': 'Durează de obicei câteva secunde.',
  'success.confirming.action': 'Verifică starea facturării',
  'success.confirmed.subscription.title': 'PRO deblocat',
  'success.confirmed.subscription.description': 'Ai acum extindere nelimitată, taie-și-continuă, creștere dificultate, traducere și ilustrații.',
  'success.confirmed.subscription.renews': 'Se reînnoiește {date}',
  'success.confirmed.creditPack.title': 'Ai cumpărat „{packName}” — +{count, plural, one {# credit} few {# credite} other {# de credite}}',
  'success.action.start': 'Începe să folosești PRO',
  'success.action.continue': 'Continuă',
  'success.stillConfirming.title': 'Încă se confirmă…',
  'success.stillConfirming.description': 'Durează mai mult decât de obicei. Achiziția ta este în siguranță — revino aici într-un minut sau reîncarcă pagina.',
  'success.stillConfirming.action': 'Mergi la Facturare',

  'error.cancelled.title': 'Nicio schimbare — ești încă pe Free',
  'error.cancelled.description': 'Plata a fost anulată. Poți încerca din nou oricând.',
  'error.cancelled.action': 'Încearcă din nou',
  'error.declined.title': 'Plată refuzată',
  'error.declined.description': 'Metoda ta de plată a fost refuzată. Actualizeaz-o și încearcă din nou.',
  'error.declined.action': 'Actualizează metoda de plată',
  'error.pastDue.title': 'Plata ta PRO nu a trecut',
  'error.pastDue.description': 'Actualizează metoda de plată până la {date} pentru a păstra PRO.',
  'error.pastDue.descriptionNoDate': 'Actualizează metoda de plată pentru a păstra PRO.',
  'error.pastDue.action': 'Actualizează metoda de plată',
  'error.backToBilling': 'Înapoi la Facturare',
};

export const CHECKOUT_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type CheckoutCatalogKey = keyof typeof en;
